import { useContext, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  TabView,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useUsers } from "../../hooks/useUsers";
import { UsersSchema } from "../../validations/schemas";
import { AppContext } from "../../context/index";
import { locationMaster, infoUsersFields, functionMaster } from "../../constants";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { ProgressBar } from 'iuvity-ui-components/dist/progressbar';
import { ApplicationRolesComponent } from "../../components/users/ApplicationRolesComponent";
import { ManagementRolesComponent } from "../../components/users/ManagementRolesComponent";
import { useTenants } from "../../hooks/useTenants";
import { useUserRole } from "../../hooks/useUserRole";

const initFormBlankValues = {
  firstName: "",
  lastName: "",
  email: "",
  location: "",
  secondaryEmail: null,
  function: "",
  status: ""
};

export const NewUserPage = ({
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedApplicationRoles, selectedManagementRoles, selectedTenantId, tenantId, selectedUserRoleInfo },
    setAppState,
  } = useContext(AppContext);

  const [changeRoles, setChangeRoles] = useState<boolean>(false);


  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: initFormBlankValues,
  });

  const {
    control,
    getValues,
    setValue,
    formState: { isDirty, isValid },
  } = form;

  const formKeysOptions: any = {
    location: locationMaster(t),
    function: functionMaster(t),
  };

  const firstnameValues = useWatch({
    control: control,
    name: "firstName",
  });

  const lastnameValues = useWatch({
    control: control,
    name: "lastName",
  });

  const emailValues = useWatch({
    control: control,
    name: "email",
  });

  const locationValues = useWatch({
    control: control,
    name: "location",
  });

  const functionValues = useWatch({
    control: control,
    name: "function",
  });

  const { userAction } = useUsers("ID");
  const { userRoleAction } = useUserRole("ACTIONS");
  const { listApps } = useTenants("LISTAPPS");
  const { listApps: listApps1 } = useTenants("LISTAPPS");
  const [infoStep, setInfoStep] = useState(selectedUserRoleInfo ? 2 : 1);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [rolesSelection, setRolesSelection] = useState(0);
  const [adminSelection, setAdminSelection] = useState(0);
  const [continueWidzard, setContinue] = useState(false);


  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedConfig: null,
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const { location, function: functionData, ...rest } = formValues;
    const data = {
      ...rest,
      tenantId: selectedTenantId || tenantId,
      attributes: { location: location, function: functionData }
    };
    const actionResult = await userAction("CREATE", data);
    if (actionResult === "SUCCESS") {
      navigate("/users");
      window.scrollTo(0, 0);
    }
  };

  const createAndContinue = () => {
    return setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t(`Users.createModalTitle`),
      content: t(`Users.createModalContent`),
      onConfirm: handleCreateAndContinue,
    }));
  }

  const handleCreateAndContinue = async () => {
    setAppState((prev) => ({
      ...prev,
      selectedApplicationRoles: [],
      selectedManagementRoles: []
    }));
    const formValues = getValues();
    const { location, function: functionData, ...rest } = formValues;
    const data = {
      ...rest,
      tenantId: selectedTenantId || tenantId,
      attributes: { location: location, function: functionData },
      secondaryEmail: rest.secondaryEmail ? rest.secondaryEmail : null
    };
    const actionResult = await userAction("CREATE", data);
    if (actionResult === "SUCCESS") {
      setValue('status', 'INACTIVE');
      setInfoStep(2);
      setActiveIndex(0);
    }
  };

  const onAssignRolesAndFinish = async () => {
    const dataApplicationRoles = selectedApplicationRoles.map((rol: any) => {
      return {
        roleId: rol.roleId,
        appId: rol.appId,
        environmentId: rol.environmentId,
        tenantId: rol.tenantId,
        expeditionDate: rol.expeditionDate ? new Date(rol.expeditionDate) : null,
        expirationDate: rol.expirationDate ? new Date(rol.expirationDate) : null,
      }
    });

    const dataManagementRoles = selectedManagementRoles.map((rol: any) => {
      return {
        roleId: rol.id,
        appId: rol.appId,
        environmentId: rol.environmentId,
        tenantId: rol.tenantId,
        expeditionDate: rol.expeditionDate ? new Date(rol.expeditionDate) : null,
        expirationDate: rol.expirationDate ? new Date(rol.expirationDate) : null,
      }
    });


    const dataRolesResult = {
      userId: selectedUserRoleInfo?.id,
      roles: [...dataApplicationRoles, ...dataManagementRoles]
    }

    const actionResult = await userRoleAction("CREATE", dataRolesResult, selectedUserRoleInfo);
    if (actionResult === "SUCCESS") {
      navigate("/users");
      window.scrollTo(0, 0);
    }
  };
  const readOnlyObject = {
    firstName: true,
    lastName: true,
    email: true,
    location: true,
    secondaryEmail: true,
    function: true,
    status: true,
    lastAccessDate: true,
  };

  const handleModalProps = (actionType: "action" | "cancel" | "rol") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Users.createModalTitle`),
          content: t(`Users.createModalContent`),
          onConfirm: onAction,
        }));
      case "rol":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Users.assignRolesModalTitle`),
          content: `${t(`Users.assignRolesModalContent1`)} "${selectedUserRoleInfo?.firstName} ${selectedUserRoleInfo?.lastName}" ${t(`Users.assignRolesModalContent2`)}`,
          onConfirm: onAssignRolesAndFinish,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Users.cancelModalTitle"),
          content: t("Users.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Users.newTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Users.usersBreadcrum"),
              disabled: true
            },
            {
              label: t("Users.usersTitle"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              command: () => onReturn(),
            },
            {
              label: t("Users.newTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull className="my-4 p-0 wrapper-form-user" style={{ backgroundColor: "transparent" }}>
        <div className="mx-5">
          <ProgressBar
            showValue={true}
            step={infoStep}
            totalSteps={2}
          />
          {infoStep === 1
            ?
            <div className="t-subheader-content" style={{ margin: "2.5rem 0 0" }}>
              <span
                dangerouslySetInnerHTML={{ __html: t(`Users.usersCreateDescription1`) }}
                style={{ lineHeight: 'var(--space-m)' }}
              />
            </div>
            :
            <div className="t-subheader-content" style={{ margin: "2.5rem 0 0" }}>
              <span>{t(`Users.usersCreateDescription3`)}</span>
            </div>
          }
        </div>
        {(infoStep === 1 && !selectedUserRoleInfo) &&

          <BoxFull parentClassname="wrapper-title-new-user" className="mt-4 p-0 form-user" style={{ backgroundColor: "transparent" }}>
            <div className="user-title-info">
              <div className="title">
                {t("Users.usersInfoTitle")}
              </div>
            </div>

            <CustomFormComponent
              functionality="Users"
              fields={infoUsersFields}
              form={form}
              schemaOfValidation={UsersSchema}
              dropdownOptions={formKeysOptions}
              REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
              tooltipTextObject={{
                email: t("Users.emailTooltip"),
                secondaryEmail: t("Users.emailTooltip")
              }}
            />
          </BoxFull>
        }


        {(infoStep === 2 && selectedUserRoleInfo && listApps) &&
          <div style={{ marginLeft: '2.5rem' }}>
            <TabView
              className="upload-tabview mt-4"
              activeIndex={activeIndex}
              onTabChange={(e) => {
                setActiveIndex(e.index);
                // if (e.index === 0 && form.formState.isDirty) form.reset();
              }}
              items={[
                {
                  key: "0",
                  header: `${t("Users.usersInfoTitle")}`,
                  children: (
                    <CustomFormComponent
                      functionality="Users"
                      fields={[...infoUsersFields, {
                        key: "status",
                        type: "text",
                        col: 2
                      }]}
                      form={form}
                      schemaOfValidation={UsersSchema}
                      dropdownOptions={formKeysOptions}
                      REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
                      readOnlyObject={readOnlyObject}
                    />
                  ),
                  disabled: false,
                },
                {
                  key: "1",
                  header: `${t("Users.applicationRoles")} (${selectedApplicationRoles ? selectedApplicationRoles.length : 0})`,
                  children: (
                    <ApplicationRolesComponent
                      data={listApps}
                      rolesSelection={rolesSelection}
                      setRolesSelection={setRolesSelection}
                      userRoleData={[]}
                      onSelectRole={setChangeRoles}

                    />
                  ),
                  disabled: false,
                },
                {
                  key: "2",
                  header: `${t("Users.managementRoles")} (${selectedManagementRoles ? selectedManagementRoles.length : 0})`,
                  children: (
                    <ManagementRolesComponent
                      data={listApps1}
                      adminSelection={adminSelection}
                      setAdminSelection={setAdminSelection}
                      userRoleData={[]}
                      onSelectRole={setChangeRoles}
                    />
                  ),
                  disabled: false,
                },
              ]}
            />
          </div>

        }
      </BoxFull>

      <BoxFull className="mt-0 mb-3 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-4 mx-5">
          <Button
            variant="p-button-text"
            label={t("Users.cancelBtnLabel")}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
          />
          {infoStep === 1 && <Button
            variant="p-button-text"
            label={
              infoStep === 1
                ? t("Users.createAndExitBtnLabel")
                : t("Users.goBackBtnLabel")
            }
            onClick={isDirty ? () => handleModalProps("action") : () => { }}
            disabled={
              infoStep === 1
                ?
                !(
                  firstnameValues?.length > 0 &&
                  lastnameValues?.length > 0 &&
                  emailValues?.length > 0 &&
                  locationValues?.length > 0 &&
                  functionValues?.length > 0 &&
                  isValid
                )
                :
                false
            }
          />}
          <Button
            variant="p-button"
            label={
              infoStep === 1
                ? t("Users.createAndContinueBtnLabel")
                : t("Users.assignRolesAndFinishLabel")
            }
            disabled={
              infoStep === 1
                ?
                !(
                  firstnameValues?.length > 0 &&
                  lastnameValues?.length > 0 &&
                  emailValues?.length > 0 &&
                  locationValues?.length > 0 &&
                  functionValues?.length > 0 &&
                  isValid
                )
                :
                !(
                  selectedApplicationRoles && selectedApplicationRoles.length > 0 ||
                  selectedManagementRoles && selectedManagementRoles.length > 0
                )
            }
            onClick={infoStep === 1 ? () => createAndContinue() : () => handleModalProps("rol")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
